import {
  Observable,
  fromEvent,
  map,
  merge,
  shareReplay,
  startWith,
  take,
  tap,
} from "rxjs";
import { FavoritesLib } from "./libFavoritter.js";
import { option } from "fp-ts";
import { pipe } from "fp-ts/lib/function.js";
import { Failure } from "src/js/helpers/failure.js";
import { LanguageService } from "src/js/services/language-service.js";
import { Toast } from "toaster-js";
import { IFavoritesService } from "./favorites-interface.js";

type FavoritesInitializedEvent = CustomEvent<{
  favorites: { post_id: number }[];
  initialLoad: boolean;
}>;

type FavoriteClickedEvent = CustomEvent<{
  postId: number;
  siteId: number;
  status: "active" | "inactive";
  favorites: { post_id: number }[];
}>;

export class SimpleFavorites implements IFavoritesService {
  private Favorites$: Observable<number[]> | null = null;
  private LanguageService: LanguageService;

  constructor(languageService: LanguageService) {
    this.LanguageService = languageService;
    if (this.Favorites$ == null) {
      this.configure();
    }
  }

  get$(): Observable<number[]> {
    if (this.Favorites$ == null) {
      Failure("Favorites have not been initialized");
    }

    return this.Favorites$;
  }
  updateDom(): void {
    setTimeout(() => {
      document.dispatchEvent(new Event("favorites-update-all-buttons"));
    }, 1);
  }

  private configure(): void {
    FavoritesLib.Factory();

    const initialFavorites$ = fromEvent(document, "favorites-initialized").pipe(
      map((event) => event as FavoritesInitializedEvent),
      map((event) => event.detail.favorites),
      take(1)
    );
    const favoritesChanged$ = fromEvent(
      document,
      "favorite-status-changed"
    ).pipe(
      tap((event) => event.stopImmediatePropagation()),
      map((event) => event as FavoriteClickedEvent),
      tap((event) => this.displayToastForEvent(event)),
      map((event) => event.detail.favorites)
    );

    this.Favorites$ = merge(initialFavorites$, favoritesChanged$).pipe(
      startWith<{ post_id: number }[]>([]),
      map((favorites) => favorites.map((favorite) => favorite.post_id)),
      tap((_) => this.updateDom()),
      shareReplay(1)
    );
    this.Favorites$?.subscribe(); // subscribe initially to make the observable run at startup
  }

  private displayToastForEvent(event: FavoriteClickedEvent): void {
    const postId = event.detail.postId;
    const wasAdded = event.detail.status == "active";
    const isTidspunkt = pipe(
      document.querySelector<Element>(`.ug-favorit[data-postid="${postId}"]`),
      option.fromNullable,
      option.getOrElseW(() =>
        Failure(`Couldn't find favorites element "[data-postid="${postId}"]"`)
      ),
      (element) => element.classList.contains("favorittidspunkt")
    );

    const textTable = this.LanguageService.getTranslations().toast;
    const text =
      isTidspunkt && wasAdded
        ? textTable.programAdd
        : isTidspunkt && !wasAdded
        ? textTable.programRemove
        : !isTidspunkt && wasAdded
        ? textTable.favoritesAdd
        : textTable.favoritesRemove;

    new Toast(text, Toast.TYPE_INFO, 3000);
  }
}
